import { DragEvent, useState } from 'react';
import { CatalogPlugin } from './types';

/** Dragging repeats the + and − buttons: catalog to template adds, template to catalog removes. */
export const PLUGIN_DRAG_TYPE = 'application/x-pay-policy-plugin';

/** A plugin dragged from the catalog carries the version range picked on its row. */
export type PluginDrag = { origin: 'catalog'; plugin: CatalogPlugin; version: string } | { origin: 'template'; pluginId: string };

export const startPluginDrag = (event: DragEvent, drag: PluginDrag) => {
  event.dataTransfer.setData(PLUGIN_DRAG_TYPE, JSON.stringify(drag));
  event.dataTransfer.effectAllowed = drag.origin === 'catalog' ? 'copy' : 'move';
};

export const readPluginDrag = (event: DragEvent): PluginDrag | null => {
  try {
    const raw = event.dataTransfer.getData(PLUGIN_DRAG_TYPE);
    return raw ? (JSON.parse(raw) as PluginDrag) : null;
  } catch {
    return null;
  }
};

/** A drop zone that only reacts to plugin drags; `active` lights it up while one hovers over it. */
export const usePluginDropZone = (onDrop: (drag: PluginDrag) => void) => {
  const [depth, setDepth] = useState(0);
  const carriesPlugin = (event: DragEvent) => Array.from(event.dataTransfer.types).includes(PLUGIN_DRAG_TYPE);
  return {
    active: depth > 0,
    handlers: {
      onDragEnter: (event: DragEvent) => {
        if (carriesPlugin(event)) setDepth((current) => current + 1);
      },
      onDragLeave: (event: DragEvent) => {
        if (carriesPlugin(event)) setDepth((current) => Math.max(0, current - 1));
      },
      onDragOver: (event: DragEvent) => {
        if (carriesPlugin(event)) event.preventDefault();
      },
      onDrop: (event: DragEvent) => {
        setDepth(0);
        const drag = readPluginDrag(event);
        if (!drag) return;
        event.preventDefault();
        onDrop(drag);
      }
    }
  };
};
